import { UI_FONT_STACK, UI_Z } from "./uiTokens.ts";
import { COLORS } from "../core/constants.ts";
import { displayDpr } from "../utils/displayDpr.ts";
import { isCompactScreen } from "../utils/deviceTier.ts";
import type { PerfGovernorDebug } from "../engine/PerfGovernor.ts";

/**
 * PerfHud — the on-device performance read-out (F15 / Teil D).
 *
 * WHY THIS EXISTS. The owner reports iOS performance as weak, and that reading
 * is currently unmeasured while a platform decision rides on it. Two documented
 * properties can explain the impression entirely without anything being slow:
 *   · `displayDpr` caps DPR at **1** on the phone tier — a load-bearing
 *     WKWebView jetsam fix (`utils/displayDpr.ts`), so an iPhone renders at half
 *     the linear resolution of its Retina panel. Softness reads as "runs badly".
 *   · `PerfGovernor` degrades ON PURPOSE under load, so the owner may be looking
 *     at a correctly reduced picture rather than an overloaded one.
 * Neither can be told apart from a real frame-rate problem by eye. This overlay
 * shows the three numbers that separate them: frame interval, the governor's
 * current degrade rung, and the effective DPR.
 *
 * WHAT IT IS NOT. It never writes sim state and never reads one — it is a pure
 * DOM read-out over render-side signals, so the golden masters cannot move.
 *
 * WHERE IT LIVES. Mounted on `document.body`, deliberately OUTSIDE
 * `#game-container`: the stage is a uniform `scale(s ≈ 0.44)` transform on a
 * phone, so an in-stage read-out would render at ~8 px and be unreadable on the
 * very device it exists to measure. Outside the transform its px are real px,
 * and it composes no `--ui-scale` / `--chrome-scale` — that also means it can
 * never take part in, or perturb, the stage layout maths.
 *
 * HOW IT IS TURNED ON (default OFF, three equivalent switches):
 *   1. `?perf=1` on the URL (`?perf=0` turns it back off) — the web build.
 *   2. `localStorage["aiw_perfhud"] = "1"` — survives reloads; the URL switch
 *      writes it, so a web session's choice carries into the next launch.
 *   3. Seven taps on the boot screen's version line (`.boot-tag .ver`) inside
 *      four seconds. This is the ONLY one reachable in the native iOS shell:
 *      Capacitor loads `dist/` from `capacitor://localhost` with no URL bar and
 *      no console, so without it the instrument could not be used on TestFlight
 *      — which is the one place the owner's question actually needs answering.
 *
 * The rAF loop runs ONLY while enabled, so a build with the switch untouched
 * pays a nul-check at boot and nothing per frame.
 *
 * The `aiw_perfhud` key is read/written through `window.localStorage` directly
 * rather than `StorageAdapter` — the same deliberate bypass, for the same
 * reason, as the existing `aiw_perf` dev toggle (`game/renderPipeline.ts:86`):
 * this is a diagnostic switch, not player state, and must not enter the
 * migrated meta-state schema.
 */

/** localStorage key for the persisted switch (see the class doc's bypass note). */
const STORAGE_KEY = "aiw_perfhud";
/** URL query parameter that sets/clears the switch. */
const URL_PARAM = "perf";
/** Frame intervals kept for the rolling average / worst-case read-out. */
const WINDOW_FRAMES = 120;
/** Taps on the version line needed to toggle, and the window they must fall in. */
const SECRET_TAPS = 7;
const SECRET_WINDOW_MS = 4000;
/** Read-out refresh period (ms) — the numbers must be readable, not strobing. */
const REDRAW_MS = 250;

/** The live governor read-out, or `null` before a run has built one. */
export type GovernorProbe = () => PerfGovernorDebug | null;

function readStoredFlag(): boolean {
  try {
    return window.localStorage.getItem(STORAGE_KEY) === "1";
  } catch {
    // Private mode / storage disabled — the switch simply does not persist.
    return false;
  }
}

function writeStoredFlag(on: boolean): void {
  try {
    if (on) window.localStorage.setItem(STORAGE_KEY, "1");
    else window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* non-fatal: the overlay still toggles for this session */
  }
}

/**
 * Resolve the initial state: an explicit `?perf=` wins and is persisted, else
 * the stored flag. Never throws — a malformed URL must not break boot.
 */
export function resolveInitialPerfHudFlag(search?: string): boolean {
  try {
    const raw = search ?? window.location.search;
    const param = new URLSearchParams(raw).get(URL_PARAM);
    if (param !== null) {
      const on = param === "1" || param === "true";
      writeStoredFlag(on);
      return on;
    }
  } catch {
    /* fall through to the stored flag */
  }
  return readStoredFlag();
}

export class PerfHud {
  private readonly el: HTMLDivElement;
  private governor: GovernorProbe = () => null;
  private enabled = false;
  private rafId: number | null = null;
  private lastTs = 0;
  private lastPaint = 0;
  /** Ring of recent frame intervals (ms). */
  private readonly frames: number[] = [];
  private frameIdx = 0;
  /** Secret-tap state (see the class doc, switch 3). */
  private tapCount = 0;
  private tapFirstAt = 0;
  private secretHost: HTMLElement | null = null;
  private secretHandler: ((e: Event) => void) | null = null;

  constructor(parent: HTMLElement = document.body) {
    this.el = document.createElement("div");
    this.el.id = "perf-hud";
    this.el.setAttribute("aria-hidden", "true");
    this.el.style.cssText = [
      "position:fixed",
      "top:6px",
      "left:6px",
      `z-index:${String(UI_Z.TOOLTIP)}`,
      "pointer-events:none",
      "display:none",
      "padding:5px 8px",
      "border-radius:5px",
      "background:rgba(0,0,0,.72)",
      "border:1px solid rgba(136,224,208,.45)",
      // Identity green from the palette module, never an inline hex (D-225).
      `color:${COLORS.SENTINEL_GREEN}`,
      `font-family:${UI_FONT_STACK}`,
      // A literal px size, NOT withUiScale(): this overlay lives outside the
      // scaled stage on purpose (see the class doc), so it must not compose the
      // stage's type knobs.
      "font-size:13px",
      "line-height:1.35",
      "letter-spacing:.04em",
      "white-space:pre",
      "text-align:left",
    ].join(";");
    parent.appendChild(this.el);
  }

  /** Wire the live governor read-out. Safe to call before any run exists. */
  attachGovernor(probe: GovernorProbe): void {
    this.governor = probe;
  }

  isEnabled(): boolean {
    return this.enabled;
  }

  /** Apply a state and persist it. */
  setEnabled(on: boolean): void {
    if (on === this.enabled) return;
    this.enabled = on;
    writeStoredFlag(on);
    this.el.style.display = on ? "block" : "none";
    if (on) {
      this.frames.length = 0;
      this.frameIdx = 0;
      this.lastTs = 0;
      this.lastPaint = 0;
      this.render();
      this.loop();
    } else if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  toggle(): void {
    this.setEnabled(!this.enabled);
  }

  /**
   * Install the on-device switch: {@link SECRET_TAPS} taps on the boot screen's
   * version line inside {@link SECRET_WINDOW_MS}. Delegated from `host` (the
   * stage root) rather than bound to the element itself, because the boot screen
   * rebuilds its own `innerHTML` on every language change and a direct listener
   * would not survive that. The version line carries no other handler, so this
   * adds no behaviour to an existing control.
   */
  installSecretToggle(host: HTMLElement): void {
    this.uninstallSecretToggle();
    const handler = (e: Event): void => {
      const target = e.target as HTMLElement | null;
      if (!target?.closest?.(".boot-tag .ver")) return;
      const now = Date.now();
      if (now - this.tapFirstAt > SECRET_WINDOW_MS) {
        this.tapFirstAt = now;
        this.tapCount = 0;
      }
      this.tapCount += 1;
      if (this.tapCount >= SECRET_TAPS) {
        this.tapCount = 0;
        this.tapFirstAt = 0;
        this.toggle();
      }
    };
    host.addEventListener("click", handler);
    this.secretHost = host;
    this.secretHandler = handler;
  }

  uninstallSecretToggle(): void {
    if (this.secretHost && this.secretHandler) {
      this.secretHost.removeEventListener("click", this.secretHandler);
    }
    this.secretHost = null;
    this.secretHandler = null;
  }

  /** Test/teardown hook — stops the loop and removes the node. */
  destroy(): void {
    this.setEnabled(false);
    this.uninstallSecretToggle();
    this.el.remove();
  }

  /** The rendered text (exposed so a test can assert content without pixels). */
  readout(): string {
    return this.el.textContent ?? "";
  }

  private loop = (): void => {
    if (!this.enabled) return;
    this.rafId = requestAnimationFrame((ts: number) => {
      if (this.lastTs > 0) {
        const dt = ts - this.lastTs;
        // Drop the first frame after a background/foreground gap: a multi-second
        // interval is the OS pausing us, not a stutter, and it would poison the
        // average the owner is asked to read.
        if (dt > 0 && dt < 1000) {
          this.frames[this.frameIdx % WINDOW_FRAMES] = dt;
          this.frameIdx += 1;
        }
      }
      this.lastTs = ts;
      if (ts - this.lastPaint >= REDRAW_MS) {
        this.lastPaint = ts;
        this.render();
      }
      this.loop();
    });
  };

  private render(): void {
    const n = this.frames.length;
    let avg = 0;
    let worst = 0;
    for (const f of this.frames) {
      avg += f;
      if (f > worst) worst = f;
    }
    avg = n > 0 ? avg / n : 0;
    const fps = avg > 0 ? 1000 / avg : 0;

    const gov = this.governor();
    const govLine =
      gov === null
        ? "DEGRADE  — (no run)"
        : `DEGRADE  L${String(gov.level)} ${gov.rungs}  motion ${gov.motion.toFixed(2)}  fx ${gov.postFx}`;

    const rawDpr =
      typeof window !== "undefined" && window.devicePixelRatio
        ? window.devicePixelRatio
        : 1;
    const fit = (window as unknown as { __stageFit?: { scale?: number } })
      .__stageFit;
    const stageScale = typeof fit?.scale === "number" ? fit.scale : NaN;
    const chromeNum = readChromeScale();

    this.el.textContent = [
      "DIAG · AI WARS",
      `FRAME    ${avg.toFixed(1)} ms avg · ${worst.toFixed(1)} ms worst · ${fps.toFixed(0)} fps  (n=${String(n)})`,
      govLine,
      `DPR      ${displayDpr().toFixed(0)} effective / ${rawDpr.toFixed(1)} device${isCompactScreen() ? " · phone tier" : ""}`,
      `STAGE    scale ${Number.isFinite(stageScale) ? stageScale.toFixed(4) : "n/a"} · chrome ${Number.isFinite(chromeNum) ? chromeNum.toFixed(4) : "n/a"}`,
    ].join("\n");
  }
}

/**
 * `--chrome-scale` is declared on `#game-container` (index.html:117), NOT on the
 * root element — reading it off `document.documentElement` returns an empty
 * string and silently reports 1 on a phone that is actually boosted. Read the
 * stage, or do not report the number at all.
 */
function readChromeScale(): number {
  const stage =
    typeof document !== "undefined"
      ? document.getElementById("game-container")
      : null;
  if (!stage) return NaN;
  return Number(getComputedStyle(stage).getPropertyValue("--chrome-scale"));
}
